import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, catchError, map, of, switchMap, throwError } from "rxjs";
import { ACCOUNTING_ENV } from "../../config/micros-env.tokens";
import { EnvMicroAccounting } from "../../config/env.micro.accounting";
import { ReconciliationDayBalance } from "../../../domain/models/accounting/reconciliation-day.model";
import { ReconciliationDaysPort } from "../../../app/ports/accounting/reconciliation-days.port";
import {
  toApplicableDayId,
  toReconciliationDayBalance,
} from "../../validators/accounting/reconciliation-day.validator";

const INVALID_DAY = "La respuesta de la caja de conciliación es inválida.";

@Injectable()
export class ReconciliationDaysHttpAdapter implements ReconciliationDaysPort {
  private static readonly PATH = "/api/v1/reconciliation-days";

  constructor(
    private readonly http: HttpClient,
    @Inject(ACCOUNTING_ENV) private readonly env: EnvMicroAccounting,
  ) {}

  currentDay(idCompany: number, idCurrency: number, businessDate: string): Observable<ReconciliationDayBalance | null> {
    const params = new HttpParams()
      .set("idCompany", idCompany)
      .set("idCurrency", idCurrency)
      .set("businessDate", businessDate)
      .set("openIfMissing", false);
    return this.http.get<unknown>(this.url("/applicable"), { params }).pipe(
      map((response) => {
        const idDay = toApplicableDayId(response);
        if (idDay === null) throw new Error(INVALID_DAY);
        return idDay;
      }),
      switchMap((idDay) => this.http.get<unknown>(this.url(`/${idDay}`))),
      map((response) => ReconciliationDaysHttpAdapter.balance(response)),
      catchError((error: unknown) => ReconciliationDaysHttpAdapter.nullWhenMissing(error)),
    );
  }

  dayOn(idCompany: number, idCurrency: number, businessDate: string): Observable<ReconciliationDayBalance | null> {
    const params = new HttpParams()
      .set("idCompany", idCompany)
      .set("idCurrency", idCurrency)
      .set("businessDate", businessDate);
    return this.http.get<unknown>(this.url("/by-date"), { params }).pipe(
      map((response) => ReconciliationDaysHttpAdapter.balance(response)),
      catchError((error: unknown) => ReconciliationDaysHttpAdapter.nullWhenMissing(error)),
    );
  }

  private static balance(response: unknown): ReconciliationDayBalance {
    const day = toReconciliationDayBalance(response);
    if (!day) throw new Error(INVALID_DAY);
    return day;
  }

  private static nullWhenMissing(error: unknown): Observable<null> {
    return error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound
      ? of(null)
      : throwError(() => error);
  }

  private url(suffix: string): string {
    const baseUrl = `${this.env.host}${this.env.apiBasePath}`.replace(/\/$/, "");
    return `${baseUrl}${ReconciliationDaysHttpAdapter.PATH}${suffix}`;
  }
}
